import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgxNotifyService } from '@shared/services/ngx-notify/ngx-notify.service';
import { ResearcherService } from '../../services/researcher.service';
import { Researcher } from '@features/admin/researcher-management/models/researcher.model';

function atLeastOneRoleValidator(ctrl: AbstractControl): ValidationErrors | null {
  const g = ctrl as FormGroup;
  const anySelected = ['isResearchPersonnel','isPrincipalInvestigator','isNtrIrbCoordinator']
    .some(c => !!g.get(c)?.value);
  return anySelected ? null : { rolesRequired: true };
}

@Component({
  selector: 'app-researcher-add',
  templateUrl: './researcher-add.component.html',
  styleUrls: ['./researcher-add.component.scss']
})
export class ResearcherAddComponent implements OnInit {
  researcherForm!: FormGroup;
  isLoading = false;

  constructor(
    private fb: FormBuilder,
    private researcherService: ResearcherService,
    private notify: NgxNotifyService,
    private dialogRef: MatDialogRef<ResearcherAddComponent>,
    @Inject(MAT_DIALOG_DATA) public data?: { role: string }
  ) {}

  ngOnInit(): void {
    this.researcherForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.maxLength(100)]],
      lastName: ['', [Validators.required, Validators.maxLength(100)]],
      email: ['', [Validators.email, Validators.maxLength(255)]],
      phone: ['', [Validators.pattern(/^\d{10}$/)]],
      // roles (all unchecked by default)
      isResearchPersonnel: [false],
      isPrincipalInvestigator: [false],
      isNtrIrbCoordinator: [false]
    }, { validators: atLeastOneRoleValidator });
  }

  addResearcher(): void {
    if (this.researcherForm.invalid) {
      this.researcherForm.markAllAsTouched();
      if (this.researcherForm.errors?.rolesRequired) {
        this.notify.error('Select at least one Role');
      } else {
        this.notify.error('Please fill in all required fields correctly');
      }
      return;
    }

    const formData = this.researcherForm.value as Researcher & any;
    const roles: string[] = [];
    if (formData.isResearchPersonnel) roles.push('ResearchPersonnel');
    if (formData.isPrincipalInvestigator) roles.push('PrincipalInvestigator');
    if (formData.isNtrIrbCoordinator) roles.push('NTRIRBCoordinator');

    const researcherDto: any = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email || "",
      phone: formData.phone || "",
      // isActive derived from having >=1 role
      isActive: roles.length > 0,
      roles: roles
    };

    this.isLoading = true;
    this.researcherService.create(researcherDto).subscribe({
      next: () => {
        this.notify.success('User added successfully');
        this.isLoading = false;
        this.dialogRef.close(true);
      },
      error: err => {
        const errorMessage = err?.error?.extendedMessage || 'Failed to create User';
        this.notify.error(errorMessage);
        this.isLoading = false;
      }
    });
  }

  getErrorMessage(fieldName: string): string {
    const control = this.researcherForm.get(fieldName);
    if (control?.hasError('required')) return `${fieldName} is required`;
    if (control?.hasError('email')) return 'Please enter a valid email address';
    if (control?.hasError('pattern')) return 'Phone must be exactly 10 digits';
    if (control?.hasError('maxLength')) return `${fieldName} is too long`;
    return '';
  }
}
